import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { useTranslation } from 'react-i18next';

const EmailSender = () => {
  const { t } = useTranslation('emailsender');
  const { api, user } = useAuth();
  const [recipientType, setRecipientType] = useState('parents');
  const [parents, setParents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const fileInputRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Form fields
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [scheduleEmail, setScheduleEmail] = useState(false);
  const [scheduledTime, setScheduledTime] = useState('');

  // Fetch recipients based on type
  useEffect(() => {
    const fetchRecipients = async () => {
      setLoading(true);
      setError(null);
      try {
        if (recipientType === 'parents') {
          const response = await api.get(`/api/v1/admin/schools/${user.school_reg}/parents`);
          setParents(response.data.data);
        } else {
          const response = await api.get(`/api/v1/teachers/schools/${user.school_reg}/teachers`);
          setTeachers(response.data || response);
        }
      } catch (err) {
        setError(t('errorMessage', { error: err.message || 'Unknown error' }));
      } finally {
        setLoading(false);
      }
    };

    fetchRecipients();
  }, [recipientType, api, user.school_reg, t]);

  // Filter recipients based on search term
  const filteredRecipients = recipientType === 'parents'
    ? parents.filter(parent => 
        parent.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
        parent.email.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : teachers.filter(teacher => 
        teacher.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
        teacher.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (teacher.tsc_number && teacher.tsc_number.toLowerCase().includes(searchTerm.toLowerCase()))
      );

  // Handle file upload
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const newAttachments = [...attachments];
    
    files.forEach(file => {
      // Convert to base64 or handle as needed by your API
      const reader = new FileReader();
      reader.onload = (event) => {
        newAttachments.push({
          name: file.name,
          size: file.size,
          type: file.type,
          data: event.target.result
        });
        setAttachments([...newAttachments]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeAttachment = (index) => {
    const newAttachments = [...attachments];
    newAttachments.splice(index, 1);
    setAttachments(newAttachments);
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  const toggleRecipient = (recipient) => {
    const isSelected = selectedRecipients.some(r => 
      recipientType === 'parents' ? r.id === recipient.id : r.id === recipient.id
    );

    if (isSelected) {
      setSelectedRecipients(selectedRecipients.filter(r => 
        recipientType === 'parents' ? r.id !== recipient.id : r.id !== recipient.id
      ));
    } else {
      setSelectedRecipients([...selectedRecipients, recipient]);
    }
  };

  const selectAllRecipients = () => {
    setSelectedRecipients(filteredRecipients);
  };

  const clearAllRecipients = () => {
    setSelectedRecipients([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError(null);
    setSuccess(false);

    try {
      // Format recipients based on type
      let formattedRecipients;
      
      if (recipientType === 'parents') {
        formattedRecipients = selectedRecipients.map(parent => ({
          email: parent.email,
          name: parent.name,
          relation_type: parent.relation_type,
          student_name: "Student" // You might want to fetch student names or allow user input
        }));
      } else {
        // For teachers, we'll adapt to match the API structure
        formattedRecipients = selectedRecipients.map(teacher => ({
          email: teacher.email,
          name: teacher.name
        }));
      }

      // Format request body
      const requestBody = {
        recipients: formattedRecipients,
        subject,
        message,
        cc: cc ? cc.split(',').map(email => email.trim()) : [],
        bcc: bcc ? bcc.split(',').map(email => email.trim()) : [],
        attachments: attachments.map(att => att.data),
        scheduled_time: scheduleEmail ? scheduledTime : null
      };

      // Send email
      const endpoint = recipientType === 'parents' 
        ? '/api/v1/sms/api/v1/email/send-to-parents'
        : '/api/v1/sms/api/v1/email/send-to-teachers';
        
      await api.post(endpoint, requestBody);
      
      setSuccess(true);
      
      // Reset form after successful submission
      setSelectedRecipients([]);
      setSubject('');
      setMessage('');
      setCc('');
      setBcc('');
      setAttachments([]);
      setScheduleEmail(false);
      setScheduledTime('');
      
      // Show success message temporarily
      setTimeout(() => setSuccess(false), 5000);
      
    } catch (err) {
      setError(t('emailSendError', { error: err.message || 'Unknown error' }));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-2 max-w-7xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">{t('title')}</h1>

      {/* Error and Success Messages */}
      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded">
          <p>{error}</p>
        </div>
      )}
      
      {success && (
        <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-6 rounded">
          <p>{t('successMessage')}</p>
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-6">
        {/* Left Side - Recipients */}
        <div className="w-full md:w-1/3">
          {/* Recipient Type Selection */}
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              {t('recipientType.label')}
            </label>
            <div className="flex space-x-2">
              <button
                type="button"
                className={`px-4 py-2 rounded-md ${
                  recipientType === 'parents'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                } transition-colors flex-1`}
                onClick={() => {
                  setRecipientType('parents');
                  setSelectedRecipients([]);
                  setSearchTerm('');
                }}
              >
                {t('recipientType.parents')}
              </button>
              <button
                type="button"
                className={`px-4 py-2 rounded-md ${
                  recipientType === 'teachers'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                } transition-colors flex-1`}
                onClick={() => {
                  setRecipientType('teachers');
                  setSelectedRecipients([]);
                  setSearchTerm('');
                }}
              >
                {t('recipientType.teachers')}
              </button>
            </div>
          </div>

          {/* Search Box */}
          <div className="mb-4">
            <div className="relative">
              <input
                type="text"
                placeholder={t('search.placeholder')}
                className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Recipients Selection */}
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <label className="block text-gray-700 text-sm font-bold">
                {t('recipients.label', { count: selectedRecipients.length })}
              </label>
              <div className="space-x-2">
                <button
                  type="button"
                  className="text-blue-600 hover:text-blue-800 text-sm"
                  onClick={selectAllRecipients}
                >
                  {t('recipients.selectAll')}
                </button>
                <button
                  type="button"
                  className="text-red-600 hover:text-red-800 text-sm"
                  onClick={clearAllRecipients}
                >
                  {t('recipients.clearAll')}
                </button>
              </div>
            </div>

            <div className="border border-gray-300 rounded-md p-2 h-96 overflow-y-auto bg-gray-50">
              {loading ? (
                <div className="flex justify-center items-center h-24">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : filteredRecipients.length === 0 ? (
                <p className="text-gray-500 italic p-4 text-center">
                  {searchTerm ? t('recipients.noResults') : t('recipients.noRecipients')}
                </p>
              ) : (
                <div className="space-y-2">
                  {filteredRecipients.map((recipient) => (
                    <div
                      key={recipient.id}
                      className={`border rounded-md p-2 cursor-pointer ${
                        selectedRecipients.some((r) => r.id === recipient.id)
                          ? 'bg-blue-100 border-blue-300'
                          : 'hover:bg-gray-100'
                      }`}
                      onClick={() => toggleRecipient(recipient)}
                    >
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          className="mr-2"
                          checked={selectedRecipients.some((r) => r.id === recipient.id)}
                          onChange={() => {}}
                        />
                        <div className="flex-1 truncate">
                          <p className="font-medium">{recipient.name}</p>
                          <p className="text-gray-600 text-sm truncate">{recipient.email}</p>
                          <p className="text-gray-500 text-xs">
                            {recipientType === 'parents' ? recipient.relation_type : recipient.tsc_number}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Side - Message Form */}
        <div className="w-full md:w-2/3">
          <form onSubmit={handleSubmit}>
            {/* Subject */}
            <div className="mb-4">
              <label
                htmlFor="subject"
                className="block text-gray-700 text-sm font-bold mb-2"
              >
                {t('form.subject')}
              </label>
              <input
                type="text"
                id="subject"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                required
              />
            </div>

            {/* Message */}
            <div className="mb-4">
              <label
                htmlFor="message"
                className="block text-gray-700 text-sm font-bold mb-2"
              >
                {t('form.message')}
              </label>
              <textarea
                id="message"
                rows="10"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
              ></textarea>
            </div>

            {/* CC and BCC */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label
                  htmlFor="cc"
                  className="block text-gray-700 text-sm font-bold mb-2"
                >
                  {t('form.cc')}
                </label>
                <input
                  type="text"
                  id="cc"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={cc}
                  onChange={(e) => setCc(e.target.value)}
                />
              </div>
              <div>
                <label
                  htmlFor="bcc"
                  className="block text-gray-700 text-sm font-bold mb-2"
                >
                  {t('form.bcc')}
                </label>
                <input
                  type="text"
                  id="bcc"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={bcc}
                  onChange={(e) => setBcc(e.target.value)}
                />
              </div>
            </div>

            {/* Attachments */}
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                {t('form.attachments.label')}
              </label>
              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  onClick={triggerFileInput}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 flex items-center"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                    />
                  </svg>
                  {t('form.attachments.addFiles')}
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  className="hidden"
                  multiple
                />
                <span className="text-gray-500 text-sm">
                  {t('form.attachments.filesAttached', { count: attachments.length })}
                </span>
              </div>

              {/* List of attachments */}
              {attachments.length > 0 && (
                <div className="mt-2 space-y-2">
                  {attachments.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between bg-gray-100 rounded-md p-2"
                    >
                      <div className="flex items-center">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 text-gray-500 mr-2"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        <div>
                          <p className="text-sm font-medium truncate max-w-xs">{file.name}</p>
                          <p className="text-xs text-gray-500">
                            {t('form.attachments.size', { size: Math.round(file.size / 1024) })}
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeAttachment(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Schedule Email */}
            <div className="mb-6">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="scheduleEmail"
                  className="mr-2"
                  checked={scheduleEmail}
                  onChange={(e) => setScheduleEmail(e.target.checked)}
                />
                <label
                  htmlFor="scheduleEmail"
                  className="text-gray-700 text-sm font-bold"
                >
                  {t('form.schedule.checkbox')}
                </label>
              </div>
              
              {scheduleEmail && (
                <div className="mt-2">
                  <label
                    htmlFor="scheduledTime"
                    className="block text-gray-700 text-sm mb-2"
                  >
                    {t('form.schedule.dateTime')}
                  </label>
                  <input
                    type="datetime-local"
                    id="scheduledTime"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={scheduledTime}
                    onChange={(e) => setScheduledTime(e.target.value)}
                    required={scheduleEmail}
                  />
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={selectedRecipients.length === 0 || sending}
                className={`px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  (selectedRecipients.length === 0 || sending) ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {sending ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {t('form.buttons.sending')}
                  </div>
                ) : scheduleEmail ? (
                  t('form.buttons.schedule')
                ) : (
                  t('form.buttons.send')
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EmailSender;